import { connect } from 'react-redux';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { get } from 'lodash';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';

import { openSmallModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import { Button, Dropdown, Franco } from '@commons/utils/styledLibraryComponents';
import { DATAGRID_LOCALES } from '@commons/utils/dataGrid';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getTheme } from '@commons/utils/theme';
import { getUserTimezone } from '@commons/utils/date';
import { numberToFixed } from '@commons/utils/format';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import DisplayNumber from '@commons/DisplayNumber';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';
import StoresWithActiveProperty from '@commons/DropdownItems/StoresWithActiveProperty';

import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';

import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

import { forecast as forecastService } from '@services/forecast';

import { transformDataToTreeRows, formatDataForExport } from './formatData';

import {
  ButtonContainer,
  Container,
  ContentContainer,
  DataGridStyles,
  FlexedGapContainer,
  GridContainer,
  SidePaddedGapContainer,
} from './styledComponents';

import { getActions } from './actions';
import { getColumns, getGroupingColumn } from './columns';

const PERIOD_NB_DAYS_DEFAULT = 8;
const USER_TIMEZONE = getUserTimezone();
const YESTERDAY = moment.tz(USER_TIMEZONE).subtract(1, 'days').endOf('day');

const PastMixSimpleModifierManagement = (props) => {
  const {
    match,
    stores,
    currency,
    openSmallModal,
    showErrorMessage,
    client: { useTtc, storeName },
  } = props;

  const theme = getTheme();
  const firstActiveStore = stores.find(({ active }) => active);
  const defaultSelectedStores = firstActiveStore ? [firstActiveStore] : [];

  const apiRef = useGridApiRef();
  const [isLoading, setIsLoading] = useState(false);
  const [treeData, setTreeData] = useState([]);
  const [exportData, setExportData] = useState(null);
  const [selectedStores, setSelectedStores] = useState(defaultSelectedStores);
  const [headerData, setHeaderData] = useState({});

  const defaultStartDate = moment
    .tz(USER_TIMEZONE)
    .subtract(PERIOD_NB_DAYS_DEFAULT, 'days')
    .startOf('day');
  const [maxFutureDate, setMaxFutureDate] = useState(YESTERDAY);

  const periodPickerState = usePeriodDatePickerState(
    defaultStartDate,
    maxFutureDate,
    USER_TIMEZONE,
  );

  const getPastMixData = async () => {
    setIsLoading(true);

    try {
      const startDate = moment
        .tz(periodPickerState.startDate, USER_TIMEZONE)
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);
      const endDate = moment
        .tz(periodPickerState.endDate, USER_TIMEZONE)
        .format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY);

      const selectedStoreIds = selectedStores.map(({ id }) => id);

      const pastMix = await forecastService.getPastProductMix(selectedStoreIds, startDate, endDate);

      setTreeData(transformDataToTreeRows(pastMix));
      setExportData(formatDataForExport(pastMix));

      setHeaderData({
        turnover: get(pastMix, 'turnover', null),
        turnoverExcludingTaxes: get(pastMix, 'turnoverExcludingTaxes', null),
        margin: get(pastMix, 'margin.total', null),
        ratio: safeNumberToFixed(get(pastMix, 'margin.ratioToTurnover', null), 2, null, ' %'),
      });
    } catch (error) {
      setTreeData([]);
      setExportData(null);
      showErrorMessage(i18next.t('FORECAST.PAST_MIX.FETCH_DATA_FAILURE'));
    } finally {
      setIsLoading(false);
    }
  };

  const actions = getActions({
    exportData,
    useTtc,
    currency,
    storeName,
    startDate: periodPickerState.startDate,
    endDate: periodPickerState.endDate,
    selectedStores,
    openSmallModal,
  });

  const getCalendarInfo = () => ({
    globalInfo: i18next.t('FORECAST.PAST_MIX.DATEPICKER_TOOLTIP', {
      storeNameSingle: getClientStoreNameTranslation(storeName, false).toLowerCase(),
      storeNamePlural: getClientStoreNameTranslation(storeName, true).toLowerCase(),
    }),
  });

  // If the user selects more than one store, and the period is more than 31 days, set the period to the last 31 days
  useEffect(() => {
    const periodDiff = periodPickerState.endDate.diff(periodPickerState.startDate, 'days');

    if (selectedStores.length > 1 && periodDiff > 31) {
      periodPickerState.handleSelectedDates(
        {
          startDate: defaultStartDate,
          endDate: YESTERDAY,
        },
        true,
      );
    }
  }, [selectedStores]);

  // if one store is selected, user can select a period up to 1 year
  // if more than one store is selected, user can select a period up to 1 month
  useEffect(() => {
    const maxEndDate = moment
      .tz(periodPickerState.startDate, USER_TIMEZONE)
      .add(1, selectedStores.length > 1 ? 'month' : 'year');

    setMaxFutureDate(moment.min(YESTERDAY, maxEndDate));
  }, [periodPickerState.startDate]);

  useEffect(() => {
    if (!periodPickerState.startDate || !periodPickerState.endDate || !selectedStores.length) {
      return;
    }

    getPastMixData();
  }, [selectedStores, periodPickerState.startDate, periodPickerState.endDate]);

  const columns = getColumns();
  const groupingColumn = getGroupingColumn();

  // Get row class name based on tree depth (path length) and whether it has children
  const getRowClassName = (params) => {
    const depth = params.row.path ? params.row.path.length - 1 : 0;
    const rowHasChildren = params.row.hasChildren;
    const classes = [`tree-depth-${depth}`];

    if (rowHasChildren) {
      classes.push('tree-row-clickable');
    }

    return classes.join(' ');
  };

  // Safe wrapper for numberToFixed to handle non-numeric values
  const safeNumberToFixed = (value, nbDecimals = 2, defaultValue = '0', unit) => {
    if (value == null) {
      return defaultValue;
    }

    const valueToProcess = typeof value === 'string' ? Number(value) : value;

    // Check if valueToProcess is a valid number
    if (typeof valueToProcess !== 'number' || isNaN(valueToProcess)) {
      return defaultValue;
    }

    return numberToFixed(value, nbDecimals, defaultValue, unit);
  };

  return (
    <Container>
      <NavigationBreadCrumb featurePath={match.path} />
      <ContentContainer>
        <SidePaddedGapContainer>
          <LeftRightSplitter
            left={
              <FlexedGapContainer>
                <Dropdown
                  iconSrc="/images/inpulse/pin-black-small.svg"
                  isDisabled={isLoading}
                  isUniqueSelection={false}
                  items={stores.map((store) => ({
                    ...store,
                    renderValue: () => StoresWithActiveProperty(store),
                  }))}
                  searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                  selectedItems={selectedStores}
                  isRequired
                  onSelectionChange={setSelectedStores}
                />
                <PeriodDatePicker
                  calendarInfo={getCalendarInfo()}
                  disabled={isLoading}
                  endDate={periodPickerState.endDate}
                  focusedDateInput={periodPickerState.focusedDateInput}
                  maxFutureDate={maxFutureDate}
                  setFocusedDateInput={periodPickerState.setFocusedDateInput}
                  startDate={periodPickerState.startDate}
                  timezone="Europe/Paris"
                  onDatesChange={periodPickerState.handleSelectedDates}
                />
                <Franco
                  content={
                    <DisplayNumber
                      number={get(headerData, useTtc ? 'turnover' : 'turnoverExcludingTaxes', null)}
                      displayCurrencyCode
                    />
                  }
                  infoTooltip={`${i18next.t(
                    useTtc
                      ? 'FORECAST.PRODUCT_MIX.TURNOVER_BOX_TOOLTIP_TEXT'
                      : 'FORECAST.PRODUCT_MIX.TURNOVER_EXCLUDING_TAXES_BOX_TOOLTIP_TEXT',
                  )}`}
                  title={`${i18next.t(
                    useTtc ? 'GENERAL.TURNOVER_TAX_INCLUDED' : 'GENERAL.TURNOVER_EX_TAX',
                  )} :`}
                  width={'248px'}
                  hasBorder
                />
                <Franco
                  background={theme.colors.greys.dark}
                  color={theme.colors.greys.lightest}
                  content={
                    <DisplayNumber
                      number={get(headerData, 'margin', null)}
                      displayCurrencyCode
                      shortenDigits
                    />
                  }
                  infoTooltip={i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_TOOLTIP')}
                  labelContent={get(headerData, 'ratio', '')}
                  title={`${i18next.t('GENERAL.METRIC_MARGIN_EX_TAX_WITHOUT_CURRENCY')} :`}
                  width={'248px'}
                  hasBorder
                />
              </FlexedGapContainer>
            }
            right={
              <ButtonContainer>
                <Button
                  actions={actions}
                  color={'inpulse-outline'}
                  iconDropdown={'/images/inpulse/carret-black.svg'}
                  iconOnLeft={false}
                  label={i18next.t('GENERAL.ACTIONS')}
                />
              </ButtonContainer>
            }
          />
        </SidePaddedGapContainer>
        <GridContainer>
          <DataGridPro
            $theme={theme}
            apiRef={apiRef}
            columns={columns}
            defaultGroupingExpansionDepth={0}
            getRowClassName={getRowClassName}
            getTreeDataPath={(row) => row.path}
            groupingColDef={groupingColumn}
            initialState={{
              sorting: {
                sortModel: [{ field: 'turnoverExcludingTaxes', sort: 'desc' }],
              },
            }}
            loading={isLoading}
            localeText={
              DATAGRID_LOCALES[i18next.language]?.components?.MuiDataGrid?.defaultProps?.localeText
            }
            rows={treeData}
            sortingOrder={['desc', 'asc', null]}
            sx={DataGridStyles(theme)}
            disableColumnFilter
            disableColumnMenu
            disableColumnPinning
            disableColumnReorder
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            hideFooter
            treeData
            onRowClick={(params) => {
              const rowId = params.id;
              if (params.row.hasChildren) {
                // Get current expansion state from the DataGrid API
                const rowNode = apiRef.current.getRowNode(rowId);
                const isCurrentlyExpanded = rowNode?.childrenExpanded ?? false;
                // Toggle expansion: if expanded, collapse it; if collapsed, expand it
                apiRef.current.setRowChildrenExpansion(rowId, !isCurrentlyExpanded);
              }
            }}
          />
        </GridContainer>
      </ContentContainer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  userRights: state.baseReducer.userRights,
  stores: getSalesPointStores(state.baseReducer.stores),
  client: getClientInfo(state.baseReducer.user),
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openSmallModal: (params) => {
    dispatch(openSmallModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PastMixSimpleModifierManagement);
